

import React, { useState, useEffect } from 'react';
import { useTheme, Grid, Paper, Box, Typography, Fab, Tooltip, CircularProgress,
        Card, CardContent, CardActions, Alert } from '@mui/material';
import { Refresh as RefreshIcon, ExpandMore as ExpandMoreIcon, 
        ChevronRight as ChevronRightIcon, DataObject as DataObjectIcon, TableView as TableViewIcon, FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { TreeView } from '@mui/x-tree-view';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { alpha } from '@mui/material/styles';
import { Allotment } from "allotment";
import "allotment/dist/style.css";
import { useS3Manager } from '../../hooks/useS3Manager';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';

interface ISurveyItem {
  key: string;
  client: string;
  year: string;
  surveyId: string;
  fileName: string;
  timestamp: string;
}

const StyledAllotment = styled(Allotment)(({ theme }) => ({
  '& .allotment-resizer-bar:hover': {
    backgroundColor: `${alpha(theme.palette.primary.main, 0.2)} !important`,
  },
  '& .allotment-resizer-bar.active': {
    backgroundColor: `${alpha(theme.palette.primary.main, 0.3)} !important`,
  },
  '& .allotment-resizer-bar.vertical::after': {
    backgroundColor: `${theme.palette.primary.main} !important`,
  },
  '& .allotment-resizer-bar.horizontal::after': {
    backgroundColor: `${theme.palette.primary.main} !important`,
  },
  // Target additional selectors that may be used
  '& .allotment-split-view-resizer:hover': {
    backgroundColor: `${alpha(theme.palette.primary.main, 0.2)} !important`,
  },
  '& .allotment-split-view-resizer.active': {
    backgroundColor: `${alpha(theme.palette.primary.main, 0.3)} !important`,
  },
  // Add the important flag to ensure our styles take precedence
  '& .allotment-split-view-resizer::after': {
    backgroundColor: `${theme.palette.primary.main} !important`,
  }
}));

export default function AsrView() {

  const theme = useTheme();  
  const { isS3initialized, getObjectsForBucket, downloadObject } = useS3Manager({ bucketName: process.env.REACT_APP_AWS_S3_ASR_BUCKET! }); 
  const [isRefreshRequired, setIsRefreshRequired] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [allItems, setAllItems] = useState<Array<string>>([]);
  const [surveyItems, setSurveyItems] = useState<Array<ISurveyItem>>([]);
  const [selectedNode, setSelectedNode] = useState<{ client: string, year: string } | null>(null);
  const [filteredSurveys, setFilteredSurveys] = useState<Array<ISurveyItem>>([]);

  useEffect(() => {
    if (!isS3initialized || !isRefreshRequired) return;

    const getItems = async () => {

      setIsLoading(true);

      // Fetch objects from S3 bucket
      const prefix = `${process.env.REACT_APP_AWS_S3_ASR_PATH!}`;
      const items = await getObjectsForBucket(process.env.REACT_APP_AWS_S3_ASR_BUCKET!, prefix);

      // Process items to extract client, year, and survey information
      const processedItems = processS3Items(items);

      // Set state
      setAllItems(items);
      setSurveyItems(processedItems);
      setIsRefreshRequired(false);
      setIsLoading(false);

      console.log(`AsrView.useEffect :: getting s3 items :: total count: ${items.length}`);
    };

    getItems();

  }, [isS3initialized, isRefreshRequired]);


  const processS3Items = (items: string[]): ISurveyItem[] => {
    
    // console.log("Processing items:", items); // Debug log
    
    return items
      .filter(item =>
        (item.endsWith('.csv') || item.endsWith('.json')) &&
        (item.includes('/resp_unmapped/') || item.includes('/resp_mapped/')))
      .map(item => {
        
        // Remove the prefix path
        const pathWithoutPrefix = item.replace(`${process.env.REACT_APP_AWS_S3_ASR_PATH!}/`, '');
        // console.log("Path without prefix:", pathWithoutPrefix); // Debug log
        
        // Split the remaining path
        const parts = pathWithoutPrefix.split('/');
        
        // Extract client, year, and survey values from parts with prefixes
        const clientPart = parts.find(p => p.startsWith('client='));
        const yearPart = parts.find(p => p.startsWith('year='));
        const surveyPart = parts.find(p => p.startsWith('survey='));
        
        if (clientPart && yearPart && surveyPart) {
          
          const client = clientPart.replace('client=', '');
          const year = yearPart.replace('year=', '');
          const surveyId = surveyPart.replace('survey=', '');
          const fileName = parts[parts.length - 1];
          
          // Extract timestamp from filename (format: responses_{timestamp}.json or responses_{timestamp}.csv)
          let timestamp = "";
          if (fileName.startsWith("responses_")) {
            // Split by underscore and then remove the file extension
            const parts = fileName.split('_');
            if (parts.length > 1) {
              // Take the second part (after "responses_") and remove the extension
              const timestampWithExt = parts[1];
              // Remove the extension (.csv or .json)
              timestamp = timestampWithExt.split('.')[0];
            }
          }

          console.log(`Found: Client=${client}, Year=${year}, Survey=${surveyId}, File=${fileName}, Timestamp=${timestamp}`); // Debug log
          
          return {
            key: item,
            client,
            year,
            surveyId,
            fileName,
            timestamp
          };
        }

        // console.log("Skipping item due to missing parts:", item); // Debug log
        
        return null;

      })
      .filter(Boolean) as ISurveyItem[];
  };  

  // Handle tree item click
  const handleTreeItemClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, nodeId: string) => {
    
    const [client, year] = nodeId.split('|');
    
    if (client && year) {
      
      setSelectedNode({ client, year });
      
      // Filter surveys for the selected client and year
      const filtered = surveyItems.filter(
        item => item.client === client && item.year === year
      );
      
      setFilteredSurveys(filtered);
    } else {
      setSelectedNode(null);
      setFilteredSurveys([]);
    }
  };

  // Handle refresh button click
  const handleRefreshButtonClick = () => {
    setIsRefreshRequired(true);
  };

  // Handle file download
  const handleFileDownloadClick = async (key: string) => {
    try {
      setIsLoading(true);
      
      // Find the survey item that matches the key
      const survey = surveyItems.find(item => item.key === key);
      
      if (!survey) {
        console.error("Survey not found for key:", key);
        return;
      }
      
      // Create a formatted filename: description-client-year-surveyId.extension
      const fileExtension = survey.fileName.split('.').pop() || '';
      const description = survey.fileName.endsWith('.csv') 
        ? 'survey_monkey_flat_file'
        : 'survey_monkey_api_resp';
      
      // Format the timestamp for filename if available
      const timestampStr = survey.timestamp ? `${survey.timestamp.replace(/:/g, '-')}` : '';

      // Format download filename with all required metadata
      const formattedFilename = `${description}_${survey.client}_${survey.year}_ID${survey.surveyId}_${timestampStr}.${fileExtension}`;
      
      // Pass both the original key and the formatted filename
      await downloadObject(key, formattedFilename);
      
    } catch (error) {
      console.error("Error downloading file:", error);
    } finally {
      setIsLoading(false);
    }
  };  

  // Build tree structure for clients and years
  const buildTree = () => {
    const clientYearMap = new Map<string, Set<string>>();
    
    // Group by client and year
    surveyItems.forEach(item => {
      if (!clientYearMap.has(item.client)) {
        clientYearMap.set(item.client, new Set<string>());
      }
      clientYearMap.get(item.client)?.add(item.year);
    });
    
    // Build tree items
    return Array.from(clientYearMap.entries()).map(([client, years]) => (
      <TreeItem 
        key={client} 
        nodeId={client} 
        label={client}
        sx={{
          color: theme.palette.text.primary,
          fontSize: '1rem',
          py: .4,
          '&.Mui-selected > .MuiTreeItem-content': {
            backgroundColor: `${alpha(theme.palette.background.default, 0.2)} !important`,
          },
          '&.Mui-selected > .MuiTreeItem-content:hover': {
            backgroundColor: `${alpha(theme.palette.background.default, 0.2)} !important`,
          }
        }}
      >
        {Array.from(years).map(year => (
          <TreeItem 
            key={`${client}|${year}`} 
            nodeId={`${client}|${year}`} 
            label={year}
            onClick={(e) => handleTreeItemClick(e, `${client}|${year}`)}
            sx={{
              color: theme.palette.text.primary,
              fontSize: '1rem',
              py: .4,
              '&.Mui-selected > .MuiTreeItem-content': {
                backgroundColor: `${alpha(theme.palette.background.default, 0.2)} !important`,
              },
              '&.Mui-selected > .MuiTreeItem-content:hover': {
                backgroundColor: `${alpha(theme.palette.background.default, 0.2)} !important`,
              }
            }}
          />
        ))}
      </TreeItem>
    ));
  };

  const formatTimestamp = (timestamp: string): string => {
    if (!timestamp) return 'N/A';
    
    try {
      // Create date from ISO string
      const date = new Date(timestamp);
      
      // Format as M/D/YY
      const month = date.getMonth() + 1; // getMonth() is 0-indexed
      const day = date.getDate();
      const year = date.getFullYear().toString().slice(-2); // Last 2 digits of year
      
      // Format time as h:mm AM/PM
      let hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      
      // Convert to 12-hour format
      hours = hours % 12;
      hours = hours ? hours : 12; // Convert 0 to 12
      
      // Combine into final format: "6/10/25 10:32 AM"
      return `${month}/${day}/${year} ${hours}:${minutes} ${ampm}`;
    } catch (error) {
      console.error("Error formatting timestamp:", error);
      return timestamp; // Return the raw timestamp if parsing fails
    }
  };


  return (
    <Grid item xs={12} sx={{ position: 'relative', height: '100%' }}>
      
      {/* Loading overlay */}
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'rgba(255, 255, 255, 0.6)',
            zIndex: 999,
          }}>
          <CircularProgress />
        </div>
      )}

      {/* Main content */}
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Typography component="h1" variant="h6" >
          Admitted Student Review: Survey Monkey Responses
        </Typography>
        <Paper component='div' sx={{ width: '100%', height: '100%', m: 0, mt: 2, p: 0 }}>
          <StyledAllotment vertical={false} defaultSizes={[1, 3]}>            
            
            {/* Tree View Pane */}
            <Allotment.Pane minSize={200}>
              <TreeView
                aria-label="asr-tree-view"
                defaultExpanded={['todo', 'todo']} // Pre-expand some common clients
                defaultCollapseIcon={<ExpandMoreIcon />}
                defaultExpandIcon={<ChevronRightIcon />}
                selected={selectedNode ? `${selectedNode.client}|${selectedNode.year}` : ''}
                onNodeSelect={(event: React.SyntheticEvent, nodeId: string) => {
                  
                  // This handles selection state in the tree component
                  if (nodeId.includes('|')) { // Only set selected node if it's a year (leaf node)
                    handleTreeItemClick(event as React.MouseEvent<HTMLLIElement, MouseEvent>, nodeId);
                  }
                }}
                sx={{ 
                  overflowX: 'hidden', 
                  overflowY: 'auto',
                  maxHeight: '100%',
                  m: 2,
                  // Add these styles for selected items
                  '& .Mui-selected': {
                    backgroundColor: `${alpha(theme.palette.background.default, 1)} !important`,
                    '&:hover': {
                      backgroundColor: `${alpha(theme.palette.background.default, 0.2)} !important`,
                    },
                    '&.Mui-focused': {
                      backgroundColor: `${alpha(theme.palette.background.default, 0.15)} !important`,
                    }
                  },
                  // This targets the content inside the selected node
                  '& .Mui-selected > .MuiTreeItem-content': {
                    backgroundColor: `${alpha(theme.palette.background.default, 1)} !important`,
                  }
                }}
              >
                {buildTree()}
              </TreeView>
            </Allotment.Pane>
            
            {/* Detail Pane */}
            <Allotment.Pane snap>
              <Box 
                sx={{ 
                  height: '100%',
                  overflowY: 'auto',
                  p: 2,
                  background: theme.palette.background.default,
                }}
              >
                {selectedNode && (
                  <>
                    <Typography variant="h6" gutterBottom>
                      {`${selectedNode.client} (${selectedNode.year})`}
                    </Typography>
                    
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        mt: 2
                      }}
                    >
                      {filteredSurveys.length > 0 ? (
                        filteredSurveys.map((survey) => (
                          <Card 
                            key={survey.key} 
                            elevation={3}
                            sx={{ 
                              width: 240, 
                              height: 200, // Increased height from 180 to 200
                              display: 'flex',
                              flexDirection: 'column',
                              transition: 'transform 0.2s, box-shadow 0.2s',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: theme.shadows[6],
                              }
                            }}
                          >
                            <CardContent sx={{ flexGrow: 1, pb: 0 }}>
                              <Typography variant="body1" component="div" noWrap title={survey.surveyId} sx={{ fontSize: '1.1rem' }}>
                                Id: {survey.surveyId}
                              </Typography>
                              
                              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5 }}>
                                {survey.fileName.endsWith('.csv') ? (
                                  <TableViewIcon sx={{ fontSize: '1.2rem', color: theme.palette.grey[500], mr: 1 }} />
                                ) : (
                                  <DataObjectIcon sx={{ fontSize: '1.2rem', color: theme.palette.grey[500], mr: 1 }} />
                                )}
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    mt: .5,
                                    fontStyle: 'bold',
                                    color: theme.palette.text.secondary 
                                  }}
                                >
                                  {survey.fileName.endsWith('.csv') 
                                    ? 'Flat file for download' 
                                    : 'Bronze API response'}
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                                Timestamp: {formatTimestamp(survey.timestamp)}
                              </Typography>                              
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: .5 }}>
                                Type: {survey.fileName.split('.').pop()?.toUpperCase()}
                              </Typography>
                              {/* <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: .5 }}>
                                File: {survey.fileName}
                              </Typography> */}
                            </CardContent>
                            <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                              <Typography variant="caption" color="text.secondary">
                                {/* {new Date().toLocaleDateString()} */}
                              </Typography>
                              <Tooltip title="Download file">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleFileDownloadClick(survey.key)}
                                  sx={{ 
                                    color: theme.palette.primary.main,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    '&:hover': {
                                      backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                    }
                                  }}
                                >
                                  <FileDownloadIcon />
                                </IconButton>
                              </Tooltip>
                            </CardActions>
                          </Card>                                                
                        ))
                      ) : (
                        <Typography variant="body1">
                          No survey files found for the selected client and year.
                        </Typography>
                      )}
                    </Box>
                  </>
                )}
                
                {!selectedNode && (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: '100%'
                    }}
                  >
                    <Alert 
                      severity="info" 
                      sx={{ 
                        maxWidth: '400px',
                        boxShadow: theme.shadows[3]
                      }}
                    >
                      Select a client and year from the tree view to see available survey responses.
                    </Alert>
                  </Box>
                )}
              </Box>
            </Allotment.Pane>
          </StyledAllotment>

          {/* Refresh button */}
          <Fab 
            color="primary" 
            aria-label="refresh" 
            sx={{ position: 'absolute', bottom: '50px', right: '65px' }}
            onClick={handleRefreshButtonClick}
          >
            <RefreshIcon />
          </Fab>
        </Paper>
      </Paper>
    </Grid>
  );
}